import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { Send, MessageCircle } from "lucide-react";

export default function Inbox() {
  const { user, loading } = useAuth();
  const [threads, setThreads] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeId = searchParams.get("t");

  useEffect(() => {
    if (loading) return;
    if (!user) { loginWithGoogle("/inbox"); return; }
    refresh();
  }, [user, loading]);

  const refresh = () => api.get("/inbox/threads").then(({ data }) => setThreads(data.items || []));

  if (loading || !user) return <PageShell><div className="p-20 font-mono text-sm">Loading…</div></PageShell>;

  const activeThread = threads.find((t) => t.thread_id === activeId) || null;

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="INBOX" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-16 pb-10">
          <SectionLabel>Inbox</SectionLabel>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[0.95] tracking-tighter">
            All your conversations. <br /><span className="hl-coral">On platform.</span>
          </h1>
        </div>
      </section>

      <section className="border-b border-border min-h-[60vh]">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-px bg-border">
          {/* THREAD LIST */}
          <aside className="lg:col-span-4 bg-background min-h-[60vh]" data-testid="inbox-threads">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <p className="meta-mono">— THREADS · {threads.length}</p>
              <button onClick={refresh} className="meta-mono !text-primary">Refresh</button>
            </div>
            {threads.length === 0 ? (
              <div className="p-10 text-center text-white/55 text-sm">
                <MessageCircle size={24} className="mx-auto mb-3 text-white/30" />
                No conversations yet.
              </div>
            ) : (
              threads.map((t) => (
                <button
                  key={t.thread_id}
                  data-testid={`thread-${t.thread_id}`}
                  onClick={() => setSearchParams({ t: t.thread_id })}
                  className={`w-full text-left p-5 border-b border-border hover:bg-white/[0.03] ${activeId === t.thread_id ? "bg-white/[0.04]" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    {t.other?.picture ? <img src={t.other.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full bg-primary/30 flex items-center justify-center font-mono text-xs">{(t.other?.name || "?")[0]}</span>}
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base font-bold truncate">{t.other?.name || "Unknown"}</p>
                      <p className="meta-mono !text-white/55 truncate">{t.last_message_preview}</p>
                    </div>
                    {t.unread > 0 && <span className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                </button>
              ))
            )}
          </aside>

          {/* THREAD VIEW */}
          <div className="lg:col-span-8 bg-background flex flex-col min-h-[60vh]">
            {activeThread ? <ThreadView thread={activeThread} onSent={refresh} /> : (
              <div className="flex-1 flex items-center justify-center text-white/40">
                <p className="font-display text-xl">Select a conversation</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function ThreadView({ thread, onSent }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/inbox/threads/${thread.thread_id}/messages`).then(({ data }) => setMessages(data.items || []));
  }, [thread.thread_id]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/inbox/send", { to_user_id: thread.other.user_id, body });
      setMessages((m) => [...m, data]);
      setBody("");
      onSent();
    } finally { setSending(false); }
  };

  return (
    <>
      <header className="p-5 border-b border-border flex items-center gap-3">
        {thread.other?.picture ? <img src={thread.other.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full bg-primary/30 flex items-center justify-center font-mono text-xs">{(thread.other?.name || "?")[0]}</span>}
        <div>
          <p className="font-display text-lg font-bold">{thread.other?.name || "Unknown"}</p>
          <p className="meta-mono !text-white/55">{thread.other?.email}</p>
        </div>
      </header>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="thread-messages">
        {messages.map((m) => {
          const mine = m.from_user_id !== thread.other.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] px-4 py-3 ${mine ? "bg-primary/15 border border-primary/40" : "bg-white/[0.04] border border-border"}`}>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
                <p className="meta-mono mt-2 !text-white/40">{new Date(m.created_at).toLocaleString()}</p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="border-t border-border p-4 flex gap-2">
        <input data-testid="inbox-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a message…" className="flex-1 bg-transparent border border-border px-4 py-3 outline-none focus:border-primary text-sm" />
        <button data-testid="inbox-send" disabled={sending} className="btn-pill btn-coral"><Send size={13} /> {sending ? "Sending…" : "Send"}</button>
      </form>
    </>
  );
}
